import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '@/context/CompanyContext';
import { EmptyWorkspaceState } from '@/components/dashboard/EmptyWorkspaceState';
import { CashflowDashboard } from '@/components/cashflow/CashflowDashboard';
import { Loader as Loader2, LayoutDashboard, TrendingUp } from 'lucide-react';

// ─── Tab definitions ──────────────────────────────────────────────────────────

type DashboardTab = 'main' | 'cashflow';

interface TabDef {
  id:    DashboardTab;
  label: string;
  icon:  React.ReactNode;
}

const TABS: TabDef[] = [
  {
    id:    'main',
    label: 'ראשי',
    icon:  <LayoutDashboard className="w-4 h-4" />,
  },
  {
    id:    'cashflow',
    label: 'תזרים מזומנים',
    icon:  <TrendingUp className="w-4 h-4" />,
  },
];

// ─── Main dashboard placeholder ───────────────────────────────────────────────

function MainDashboardPlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400 gap-4">
      <LayoutDashboard className="w-16 h-16 text-gray-200" />
      <div>
        <p className="text-lg font-semibold text-gray-500">דשבורד ראשי</p>
        <p className="text-sm mt-1 text-gray-400">
          תוכן הדשבורד הראשי יוצג כאן בקרוב.
        </p>
      </div>
    </div>
  );
}

// ─── DashboardPage ────────────────────────────────────────────────────────────
//
// Reads workspace state ENTIRELY from CompanyContext (the single source of truth).
// No extra DB call here — CompanyContext.refreshCompanies() already fetched all
// companies for the current user (any role: admin, member, etc.).
// This means the empty-state is shown only when the context has truly resolved
// with zero companies, not because of a stale or role-filtered query.
//
export function DashboardPage() {
  const navigate   = useNavigate();
  const { activeCompany, activeModules, loadingCompanies } = useCompany();

  // Default to cashflow tab if available, otherwise main.
  const defaultTab: DashboardTab = activeModules.includes('cashflow') ? 'cashflow' : 'main';
  const [activeTab, setActiveTab] = useState<DashboardTab>(defaultTab);

  // If company has no cashflow module, redirect straight to the first active module.
  useEffect(() => {
    if (!loadingCompanies && activeModules.length > 0 && !activeModules.includes('cashflow')) {
      const first = activeModules[0];
      if (first === 'inventory') navigate('/app/inventory', { replace: true });
      else if (first === 'hr')   navigate('/app/employees/portfolio', { replace: true });
    }
  }, [loadingCompanies, activeModules, navigate]);

  // ── Still loading context (first mount / hard refresh) ───────────────────
  if (loadingCompanies) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">טוען דשבורד…</p>
        </div>
      </div>
    );
  }

  // ── No company after context resolved → employee has no workspace yet ─────
  if (!activeCompany) {
    return <EmptyWorkspaceState onCreateCompany={() => navigate('/onboarding')} />;
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" dir="rtl">

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
            דשבורד
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            ברוך הבא אל {activeCompany.legal_name}! בחר לשונית לצפייה בנתונים
          </p>
        </div>
      </div>

      {/* ── Tab navigation ────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-1" aria-label="Tabs">
          {TABS.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold
                  border-b-2 transition-colors whitespace-nowrap
                  ${isActive
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <span className={`transition-colors ${
                  isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                }`}>
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Tab content ───────────────────────────────────────────────────── */}
      {activeTab === 'main' && <MainDashboardPlaceholder />}
      {activeTab === 'cashflow' && <CashflowDashboard />}
    </div>
  );
}
